import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';
import EmojiAddIcon from '@atlaskit/icon/core/emoji-add';

import { ReactionsLoader } from '@confluence/reactions/entry-points/ReactionsLoader.tsx';
import {
	ReactionLocation,
	ReactionContainerType,
} from '@confluence/reactions/entry-points/reactionTypes.ts';
import { getCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
import { usePageContentId, useContentType } from '@confluence/page-context';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext.ts';

const i18n = defineMessages({
	addNew: {
		id: 'object-comments.page.reactions.add.new',
		description: 'Message displayed in button to add a new reaction.',
		defaultMessage: 'Add new',
	},
	addReaction: {
		id: 'object-comments.page.reactions.add.reaction',
		description: 'Message displayed to add a reaction.',
		defaultMessage: 'Add reaction',
	},
});

export const ObjectCommentPageReactions = ({
	isOffline = false,
	optimisticReactions = false,
}: {
	isOffline?: boolean;
	optimisticReactions?: boolean;
}) => {
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const spaceId = useSpaceId();
	const { formatMessage } = useIntl();
	const { isReactionsEnabled } = useContext(ReactionsContext);

	const { subType } = getCachedPageData(contentId);

	// Used when we want to allow the user to select reactions from the summary view popup
	const customIcon = (
		<EmojiAddIcon
			testId="emoji-add-icon"
			color={token('color.icon.subtle')}
			label={formatMessage(i18n.addReaction)}
		/>
	);

	return (
		<ReactionsLoader
			contentId={contentId || ''}
			location={
				contentType === 'blogpost'
					? ReactionLocation.OBJECT_COMMENT_FOOTER_BLOGPOST
					: ReactionLocation.OBJECT_COMMENT_FOOTER_PAGE
			}
			containerId={spaceId || ''}
			containerType={ReactionContainerType.SPACE}
			contentSubType={subType}
			summaryViewEnabled
			summaryViewThreshold={1}
			allowSelectFromSummaryView
			useButtonAlignmentStyling
			summaryViewPlacement="bottom-end"
			hideNoReactionsText
			readOnly={isOffline}
			isViewOnly={isOffline}
			isSubtle
			reactionPickerTriggerIcon={customIcon}
			reactionPickerTriggerText={formatMessage(i18n.addNew)}
			useDefaultStyledContainer={false} // we override this so we can update font size
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			hidePlaceholder={Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__)}
			noRelativeContainer={false}
			noFlexWrap={false}
			hoverableSummaryView
			reactionPickerPlacement="bottom-end"
			summaryButtonIconAfter={isReactionsEnabled ? customIcon : undefined}
			optimisticallyLoadSummaryEmojis={optimisticReactions}
			renderParticleEffectOnSummaryView
		/>
	);
};
